import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import {
  Save, Eye, Printer, Send, Wrench, Copy, X, Check, Trash2, Plus,
  MessageSquare, ChevronLeft, Cloud, FileText, Share2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { calcEstimate, emptyEstimate, inr, STATUS_META } from "./lib";
import { CustomerPicker, VehiclePicker } from "./components/Pickers";
import { LineItemTable } from "./components/LineItemTable";
import { EstimatePreview } from "./components/EstimatePreview";
import { ShareDialog } from "./components/ShareDialog";
import { ConvertDialog } from "./components/ConvertDialog";
import { estimatePdf, downloadPdf } from "@/lib/pdf";

const DEFAULT_TERMS = [
  "Prices are valid until the stated validity date.",
  "Additional work will be undertaken only after approval.",
  "Final cost may change if additional defects are identified.",
  "Parts availability is subject to confirmation.",
  "Delivery timelines depend on parts availability and work approval.",
  "Warranty, where applicable, will be governed by the respective manufacturer or supplier.",
  "Taxes will be charged as applicable.",
];

const draftKey = (id) => `fender_estimate_draft_${id || "new"}`;

/**
 * CreateEstimate — the workspace for building a customer-facing estimate.
 *
 * Layout follows the Fender design system:
 *  • Left column — customer, vehicle, complaints, parts, labour, other, terms
 *  • Right sticky column — live totals summary + primary actions
 */
export default function CreateEstimate() {
  const nav = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const isEdit = !!id;

  const [est, setEst] = useState(() => {
    // Try to hydrate from localStorage first (offline continuity)
    if (!isEdit) {
      try {
        const draft = localStorage.getItem(draftKey("new"));
        if (draft) return { ...emptyEstimate(), ...JSON.parse(draft), terms: (JSON.parse(draft).terms || DEFAULT_TERMS) };
      } catch { /* fall through to defaults */ }
      return { ...emptyEstimate(), terms: DEFAULT_TERMS };
    }
    return null;
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState(null); // 'saved' | 'saving' | 'error' | 'unsynced'
  const [showPreview, setShowPreview] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [nextNumber, setNextNumber] = useState(null);
  const autosaveTimer = useRef();

  // Load existing estimate
  useEffect(() => {
    if (!isEdit) {
      api.get("/estimates/next-number").then((r) => setNextNumber(r.data?.next_number));
      // Prefill from query string
      const customerId = params.get("customer_id");
      const vehicleId = params.get("vehicle_id");
      const jobCardId = params.get("job_card_id");
      if (customerId) {
        api.get(`/customers/${customerId}`).then((r) => {
          const c = r.data;
          setEst((prev) => ({
            ...prev, customer_id: c.id,
            customer_snapshot: {
              id: c.id, name: c.name, phone: c.phone, email: c.email || "",
              address: c.address || "",
            },
          }));
        }).catch(() => {});
      }
      if (vehicleId) {
        api.get("/vehicles").then((r) => {
          const v = (r.data || []).find((x) => x.id === vehicleId);
          if (v) setEst((prev) => ({
            ...prev, vehicle_id: v.id,
            vehicle_snapshot: {
              id: v.id, reg_number: v.reg_number, make: v.make, model: v.model,
              year: v.year, fuel_type: v.fuel_type, odometer: v.odometer,
            },
          }));
        }).catch(() => {});
      }
      if (jobCardId) {
        setEst((prev) => ({ ...prev, job_card_id: jobCardId, source: "job_card", source_ref: jobCardId }));
      }
      return;
    }
    setLoading(true);
    api.get(`/estimates/${id}`)
      .then((r) => setEst(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Estimate not found"))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // Live totals (local, decimal-safe)
  const totals = useMemo(() => calcEstimate(est || {}), [est]);

  // When we replace `est` with a fresh server response, we don't want the
  // autosave effect below to mark it dirty and re-fire — that used to create
  // an infinite "saved → unsynced → saving → saved" loop that made the Save
  // button blink and the UNSAVED chip never settle.
  const skipNextAutosaveRef = useRef(false);

  // Autosave to localStorage (offline continuity)
  useEffect(() => {
    if (!est) return;
    try { localStorage.setItem(draftKey(id || "new"), JSON.stringify(est)); } catch { /* localStorage full or unavailable */ }
  }, [est, id]);

  // Autosave to server (debounced) — only for existing estimates
  useEffect(() => {
    if (!est || !isEdit) return;
    if (est.status === "converted" || est.status === "cancelled") return;
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }
    clearTimeout(autosaveTimer.current);
    setSaveState("unsynced");
    autosaveTimer.current = setTimeout(() => {
      save({ silent: true });
    }, 2500);
    return () => clearTimeout(autosaveTimer.current);
     
  }, [est, isEdit]);

  const patchEst = (p) => setEst((prev) => ({ ...prev, ...p }));

  const save = async ({ silent = false, status = null } = {}) => {
    if (!est) return null;
    const payload = { ...est };
    if (status) payload.status = status;
    if (!payload.customer_snapshot?.name || !payload.customer_snapshot?.phone) {
      if (!silent) toast.error("Customer name and phone are required");
      return null;
    }
    setSaving(true);
    setSaveState("saving");
    try {
      let saved;
      if (isEdit) {
        const r = await api.patch(`/estimates/${id}`, payload);
        saved = r.data;
      } else {
        const r = await api.post("/estimates", payload);
        saved = r.data;
      }
      setEst(saved);
      skipNextAutosaveRef.current = true;
      setSaveState("saved");
      try { localStorage.removeItem(draftKey("new")); } catch { /* not critical */ }
      if (!silent) toast.success(`Estimate ${saved.number} saved`);
      // Navigate to the edit URL after first save
      if (!isEdit) nav(`/estimates/${saved.id}`, { replace: true });
      return saved;
    } catch (e) {
      setSaveState("error");
      if (!silent) toast.error(e.response?.data?.detail || "Save failed");
      return null;
    } finally { setSaving(false); }
  };

  const { garage: authGarage } = useAuth();
  const onPrint = async () => {
    let payload = est;
    if (!isEdit) {
      payload = await save({ silent: true });
      if (!payload) return;
    }
    const doc = estimatePdf(payload, authGarage || {});
    doc.autoPrint?.();
    const url = doc.output("bloburl");
    window.open(url, "_blank");
  };

  const onDownload = async () => {
    let payload = est;
    if (!isEdit) {
      payload = await save({ silent: true });
      if (!payload) return;
    }
    const doc = estimatePdf(payload, authGarage || {});
    const reg = (payload.vehicle_snapshot?.reg_number || payload.customer_snapshot?.name || "customer").replace(/\s+/g, "-");
    downloadPdf(doc, `Estimate_${payload.number}_${reg}.pdf`);
    await api.post(`/estimates/${payload.id}/share`, { channel: "download" }).catch(() => {});
  };

  const onDuplicate = async () => {
    if (!isEdit) return toast.info("Save the estimate first");
    const r = await api.post(`/estimates/${id}/duplicate`);
    toast.success(`Duplicated as ${r.data.number}`);
    nav(`/estimates/${r.data.id}`);
  };

  const onCancel = async () => {
    if (!isEdit) return;
    if (!window.confirm("Cancel this estimate? This cannot be undone.")) return;
    await api.post(`/estimates/${id}/cancel`);
    toast.success("Estimate cancelled");
    const r = await api.get(`/estimates/${id}`);
    setEst(r.data);
  };

  if (loading || !est) {
    return (
      <div className="p-8 text-center text-[13px] text-[#667085]">
        Loading estimate…
      </div>
    );
  }

  const isLocked = est.status === "converted" || est.status === "cancelled";
  const displayNumber = est.number || nextNumber || "DRAFT";

  return (
    <div className="pb-24 md:pb-4" data-testid="create-estimate-page">
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <button
            onClick={() => nav("/estimates")}
            data-testid="est-back"
            className="text-[11.5px] font-semibold text-[#457b9d] hover:text-[#e63946] inline-flex items-center gap-1 mb-1"
          >
            <ChevronLeft size={12} /> All estimates
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-[24px] md:text-[26px] font-bold text-[#1d3557]">
              {isEdit ? "Edit Estimate" : "New Estimate"}
            </h1>
            <span
              className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${STATUS_META[est.status]?.chip}`}
              data-testid="est-status-chip"
            >
              {STATUS_META[est.status]?.label || est.status}
            </span>
          </div>
          <div className="text-[12.5px] text-[#667085] mt-1 font-mono">
            {displayNumber}
            {est.converted_to_job_card_id && (
              <> · <Link to={`/job-cards/${est.converted_to_job_card_id}`} className="text-[#12B76A] hover:underline">View job card →</Link></>
            )}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        {/* Left column */}
        <div className="space-y-4 min-w-0">
          {/* Customer + Vehicle */}
          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="est-customer-section">
            <SectionHeader
              title="Customer Details"
              subtitle="Search an existing customer or add a new one."
            />
            <CustomerPicker
              value={est.customer_snapshot}
              disabled={isLocked}
              onChange={(c) => patchEst({
                customer_id: c.id, customer_snapshot: c,
              })}
            />
          </section>

          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="est-vehicle-section">
            <SectionHeader
              title="Vehicle Details"
              subtitle="Optional for a general estimate. Add for a job card."
            />
            <VehiclePicker
              value={est.vehicle_snapshot}
              customerId={est.customer_id || est.customer_snapshot?.id}
              disabled={isLocked}
              onChange={(v) => patchEst({
                vehicle_id: v.id, vehicle_snapshot: v,
              })}
            />
          </section>

          {/* Complaints */}
          <ComplaintsSection
            complaints={est.complaints || []}
            disabled={isLocked}
            onChange={(complaints) => patchEst({ complaints })}
          />

          {/* Line item sections */}
          <LineItemTable
            kind="parts"
            items={est.parts || []}
            taxMode={est.tax_mode}
            onChange={(parts) => patchEst({ parts })}
          />
          <LineItemTable
            kind="labor"
            items={est.labor || []}
            taxMode={est.tax_mode}
            onChange={(labor) => patchEst({ labor })}
          />
          <LineItemTable
            kind="other"
            items={est.other_charges || []}
            taxMode={est.tax_mode}
            onChange={(other_charges) => patchEst({ other_charges })}
          />

          {/* Discount + tax mode */}
          <DiscountsSection est={est} disabled={isLocked} onChange={patchEst} />

          {/* Terms + notes */}
          <TermsSection est={est} disabled={isLocked} onChange={patchEst} />
        </div>

        {/* Right sticky summary */}
        <SummaryPanel
          est={est}
          totals={totals}
          saveState={saveState}
          saving={saving}
          isLocked={isLocked}
          isEdit={isEdit}
          onSave={() => save({ status: "prepared" })}
          onSaveDraft={() => save({ status: "draft" })}
          onPreview={() => setShowPreview(true)}
          onPrint={onPrint}
          onDownload={onDownload}
          onShare={() => setShowShare(true)}
          onConvert={() => setShowConvert(true)}
          onDuplicate={onDuplicate}
          onCancel={onCancel}
        />
      </div>

      {/* Sticky mobile footer */}
      <MobileStickyFooter
        totals={totals}
        onPreview={() => setShowPreview(true)}
        onSave={() => save({ status: "prepared" })}
        saving={saving}
      />

      {showPreview && (
        <EstimatePreview
          est={{ ...est, totals }}
          onClose={() => setShowPreview(false)}
          onPrint={onPrint}
          onDownload={onDownload}
        />
      )}
      {showShare && (
        <ShareDialog
          est={est}
          onClose={() => setShowShare(false)}
          onDownload={onDownload}
        />
      )}
      {showConvert && (
        <ConvertDialog
          est={est}
          onClose={() => setShowConvert(false)}
          onConverted={(res) => {
            setShowConvert(false);
            toast.success(`Job Card ${res.job_number} created`);
            api.get(`/estimates/${id}`).then((r) => setEst(r.data));
          }}
        />
      )}
    </div>
  );
}

// ---------- Section header ----------
const SectionHeader = ({ title, subtitle }) => (
  <header className="mb-3">
    <h3 className="text-[14px] font-semibold text-[#1d3557]">{title}</h3>
    {subtitle && <p className="text-[11.5px] text-[#667085] mt-0.5">{subtitle}</p>}
  </header>
);

// ---------- Complaints ----------
function ComplaintsSection({ complaints, onChange, disabled }) {
  const [text, setText] = useState("");
  const add = () => {
    if (!text.trim()) return;
    onChange([...complaints, { id: crypto.randomUUID?.() || String(Date.now()), text: text.trim(), category: "" }]);
    setText("");
  };
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="est-complaints-section">
      <SectionHeader
        title="Customer Complaints"
        subtitle="What the customer told you. Each complaint on its own line."
      />
      <div className="flex gap-2 mb-3">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          disabled={disabled}
          placeholder="e.g. Brake pedal feels soft"
          data-testid="est-complaint-input"
          className="flex-1 h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] focus:border-[#457b9d] outline-none"
        />
        <button
          type="button"
          onClick={add}
          disabled={disabled || !text.trim()}
          data-testid="est-complaint-add"
          className="h-9 px-3 rounded-md bg-[#457b9d] hover:bg-[#3a6b8a] text-white text-[12.5px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
        >
          <Plus size={12} /> Add
        </button>
      </div>
      {complaints.length === 0 ? (
        <div className="text-[12px] text-[#a8dadc] italic">No complaints recorded yet.</div>
      ) : (
        <ul className="space-y-1.5">
          {complaints.map((c, i) => (
            <li key={c.id || i} className="flex items-start gap-2 p-2 rounded bg-[#F1FAEE]/50" data-testid={`est-complaint-item-${i}`}>
              <MessageSquare size={12} className="mt-0.5 text-[#457b9d] shrink-0" />
              <div className="flex-1 text-[13px] text-[#1d3557]">{c.text}</div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onChange(complaints.filter((_, idx) => idx !== i))}
                  className="text-[#a8dadc] hover:text-[#e63946]"
                >
                  <X size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------- Discounts + tax mode ----------
function DiscountsSection({ est, onChange, disabled }) {
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="est-discount-section">
      <SectionHeader title="Discount & Tax" subtitle="Optional overall discount applied to the gross subtotal." />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block">
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Discount</span>
          <div className="mt-1 flex gap-1">
            <input
              type="number" step="0.01" value={est.discount_value || 0}
              disabled={disabled}
              onChange={(e) => onChange({ discount_value: Number(e.target.value) })}
              data-testid="est-discount-value"
              className="flex-1 h-9 px-2 text-right font-mono text-[13px] rounded border border-[#E4E7EC] focus:border-[#457b9d] outline-none"
            />
            <select
              value={est.discount_type || "amount"}
              disabled={disabled}
              onChange={(e) => onChange({ discount_type: e.target.value })}
              data-testid="est-discount-type"
              className="h-9 px-2 rounded border border-[#E4E7EC] text-[12px]"
            >
              <option value="amount">₹</option>
              <option value="percent">%</option>
            </select>
          </div>
        </label>
        <label className="block md:col-span-2">
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Discount reason</span>
          <input
            type="text" value={est.discount_reason || ""}
            disabled={disabled}
            onChange={(e) => onChange({ discount_reason: e.target.value })}
            data-testid="est-discount-reason"
            placeholder="e.g. Loyalty customer / seasonal offer"
            className="mt-1 w-full h-9 px-2 rounded border border-[#E4E7EC] text-[13px] focus:border-[#457b9d] outline-none"
          />
        </label>
        <label className="block">
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Tax Mode</span>
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange({ tax_mode: "exclusive" })}
              data-testid="est-tax-exclusive"
              className={`flex-1 h-9 rounded border text-[12px] font-semibold ${
                est.tax_mode === "exclusive"
                  ? "bg-[#1d3557] text-white border-[#1d3557]"
                  : "bg-white text-[#667085] border-[#E4E7EC]"
              }`}
            >
              Exclusive
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange({ tax_mode: "inclusive" })}
              data-testid="est-tax-inclusive"
              className={`flex-1 h-9 rounded border text-[12px] font-semibold ${
                est.tax_mode === "inclusive"
                  ? "bg-[#1d3557] text-white border-[#1d3557]"
                  : "bg-white text-[#667085] border-[#E4E7EC]"
              }`}
            >
              Inclusive
            </button>
          </div>
        </label>
      </div>
      <div className="mt-3">
        <label className="inline-flex items-center gap-2 text-[12px] text-[#1d3557]">
          <input
            type="checkbox"
            checked={!!est.is_interstate}
            disabled={disabled}
            onChange={(e) => onChange({ is_interstate: e.target.checked })}
            data-testid="est-interstate"
          />
          Inter-state supply (use IGST instead of CGST+SGST)
        </label>
      </div>
    </section>
  );
}

// ---------- Terms + notes ----------
function TermsSection({ est, onChange, disabled }) {
  const [terms, setTerms] = useState(est.terms && est.terms.length ? est.terms : DEFAULT_TERMS);
  useEffect(() => {
    setTerms(est.terms && est.terms.length ? est.terms : DEFAULT_TERMS);
  }, [est.terms]);
  const patchTerms = (next) => { setTerms(next); onChange({ terms: next }); };
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="est-terms-section">
      <SectionHeader title="Terms, Validity & Notes" subtitle="Edit terms, set validity, and add customer / internal notes." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <label className="block">
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Estimate Date</span>
          <input
            type="date" value={est.estimate_date || ""}
            disabled={disabled}
            onChange={(e) => onChange({ estimate_date: e.target.value })}
            data-testid="est-estimate-date"
            className="mt-1 w-full h-9 px-2 rounded border border-[#E4E7EC] text-[13px] focus:border-[#457b9d] outline-none"
          />
        </label>
        <label className="block">
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Valid Until</span>
          <input
            type="date" value={est.valid_until || ""}
            disabled={disabled}
            onChange={(e) => onChange({ valid_until: e.target.value })}
            data-testid="est-valid-until"
            className="mt-1 w-full h-9 px-2 rounded border border-[#E4E7EC] text-[13px] focus:border-[#457b9d] outline-none"
          />
        </label>
      </div>
      <div className="mb-4">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] mb-2">Terms</div>
        <ul className="space-y-1.5" data-testid="est-terms-list">
          {terms.map((t, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-[12px] text-[#a8dadc] font-mono mt-1.5 w-4">{i + 1}.</span>
              <input
                type="text" value={t}
                disabled={disabled}
                onChange={(e) => patchTerms(terms.map((tt, idx) => idx === i ? e.target.value : tt))}
                data-testid={`est-term-${i}`}
                className="flex-1 h-8 px-2 rounded border border-transparent focus:border-[#457b9d] focus:bg-white text-[12.5px] outline-none"
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={() => patchTerms(terms.filter((_, idx) => idx !== i))}
                  className="text-[#a8dadc] hover:text-[#e63946] mt-1.5"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
        {!disabled && (
          <button
            type="button"
            onClick={() => patchTerms([...terms, ""])}
            className="mt-2 text-[11.5px] font-semibold text-[#457b9d] hover:text-[#e63946] inline-flex items-center gap-1"
            data-testid="est-term-add"
          >
            <Plus size={12} /> Add term
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Customer notes</span>
          <textarea
            value={est.customer_notes || ""}
            disabled={disabled}
            onChange={(e) => onChange({ customer_notes: e.target.value })}
            data-testid="est-customer-notes"
            placeholder="Visible on the printed PDF"
            rows={3}
            className="mt-1 w-full px-2 py-1.5 rounded border border-[#E4E7EC] text-[13px] focus:border-[#457b9d] outline-none resize-none"
          />
        </label>
        <label className="block">
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Internal notes</span>
          <textarea
            value={est.internal_notes || ""}
            disabled={disabled}
            onChange={(e) => onChange({ internal_notes: e.target.value })}
            data-testid="est-internal-notes"
            placeholder="Only visible to your team"
            rows={3}
            className="mt-1 w-full px-2 py-1.5 rounded border border-[#E4E7EC] bg-[#F1FAEE] text-[13px] focus:border-[#457b9d] outline-none resize-none"
          />
        </label>
      </div>
    </section>
  );
}

// ---------- Sticky summary panel (right side, desktop) ----------
function SummaryPanel({
  est, totals, saveState, saving, isLocked, isEdit,
  onSave, onSaveDraft, onPreview, onPrint, onDownload, onShare, onConvert, onDuplicate, onCancel,
}) {
  const badge = ({
    saving: { label: "Saving…", tone: "bg-[#a8dadc]/40 text-[#457b9d]" },
    saved: { label: "Saved", tone: "bg-[#12B76A]/15 text-[#12B76A]" },
    unsynced: { label: "Unsaved", tone: "bg-[#F79009]/15 text-[#F79009]" },
    error: { label: "Save failed", tone: "bg-[#e63946]/15 text-[#e63946]" },
  })[saveState] || { label: "Saved on device", tone: "bg-[#a8dadc]/30 text-[#457b9d]" };

  return (
    <aside className="hidden lg:block" data-testid="est-summary-panel">
      <div className="sticky top-4 space-y-3">
        <section className="bg-white rounded-xl border border-[#E4E7EC] p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">
              Summary
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[9.5px] font-semibold uppercase ${badge.tone}`}>
              {badge.label}
            </span>
          </div>
          <ul className="text-[12.5px] text-[#667085] space-y-1.5">
            <li className="flex justify-between"><span>Parts</span><span className="font-mono text-[#1d3557]">{inr(totals.parts_subtotal)}</span></li>
            <li className="flex justify-between"><span>Labour</span><span className="font-mono text-[#1d3557]">{inr(totals.labor_subtotal)}</span></li>
            <li className="flex justify-between"><span>Other charges</span><span className="font-mono text-[#1d3557]">{inr(totals.charges_subtotal)}</span></li>
            <li className="flex justify-between font-semibold pt-2 border-t border-[#F1FAEE]"><span>Gross subtotal</span><span className="font-mono text-[#1d3557]">{inr(totals.gross_subtotal)}</span></li>
            {Number(totals.overall_discount) > 0 && (
              <li className="flex justify-between text-[#e63946]"><span>Discount</span><span className="font-mono">− {inr(totals.overall_discount)}</span></li>
            )}
            <li className="flex justify-between"><span>Taxable</span><span className="font-mono text-[#1d3557]">{inr(totals.taxable_value)}</span></li>
            {Number(totals.igst) > 0 ? (
              <li className="flex justify-between"><span>IGST</span><span className="font-mono text-[#1d3557]">{inr(totals.igst)}</span></li>
            ) : (
              <>
                <li className="flex justify-between"><span>CGST</span><span className="font-mono text-[#1d3557]">{inr(totals.cgst)}</span></li>
                <li className="flex justify-between"><span>SGST</span><span className="font-mono text-[#1d3557]">{inr(totals.sgst)}</span></li>
              </>
            )}
            {Number(totals.rounding_adjustment) !== 0 && (
              <li className="flex justify-between text-[11.5px]"><span>Rounding</span><span className="font-mono text-[#667085]">{inr(totals.rounding_adjustment)}</span></li>
            )}
          </ul>
          <div className="mt-3 pt-3 border-t border-[#F1FAEE]">
            <div className="flex items-baseline justify-between">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Grand Total</span>
              <span className="text-[24px] font-bold font-mono tabular-nums text-[#1d3557]" data-testid="est-grand-total">
                {inr(totals.grand_total)}
              </span>
            </div>
            <div className="text-[10.5px] text-[#667085] mt-1">
              Valid until {est.valid_until || "—"}
            </div>
          </div>
        </section>

        {/* Primary actions */}
        <section className="bg-white rounded-xl border border-[#E4E7EC] p-3 space-y-2">
          {!isLocked && (
            <>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                data-testid="est-save-btn"
                className="w-full h-11 rounded-lg bg-[#e63946] hover:bg-[#d02f3c] text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Save size={14} /> {saving ? "Saving…" : "Save Estimate"}
              </button>
              <button
                type="button"
                onClick={onSaveDraft}
                disabled={saving}
                data-testid="est-save-draft-btn"
                className="w-full h-9 rounded-lg border border-[#E4E7EC] bg-white text-[12.5px] font-semibold text-[#1d3557] hover:border-[#457b9d]"
              >
                Save Draft
              </button>
            </>
          )}
          <div className="grid grid-cols-2 gap-2">
            <ActionBtn icon={Eye} label="Preview" onClick={onPreview} testId="est-preview-btn" />
            <ActionBtn icon={Printer} label="Print" onClick={onPrint} testId="est-print-btn" />
            <ActionBtn icon={FileText} label="PDF" onClick={onDownload} testId="est-download-btn" />
            <ActionBtn icon={Share2} label="Share" onClick={onShare} testId="est-share-btn" disabled={!isEdit} />
          </div>
          {isEdit && !isLocked && (
            <button
              type="button"
              onClick={onConvert}
              disabled={est.status === "converted"}
              data-testid="est-convert-btn"
              className="w-full h-11 rounded-lg bg-[#12B76A] hover:bg-[#0f9a58] text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Wrench size={14} />
              {est.status === "converted" ? "Already Converted" : "Convert to Job Card"}
            </button>
          )}
          {isEdit && (
            <div className="grid grid-cols-2 gap-2">
              <ActionBtn icon={Copy} label="Duplicate" onClick={onDuplicate} testId="est-duplicate-btn" />
              <ActionBtn icon={X} label="Cancel" onClick={onCancel} testId="est-cancel-btn" tone="red" disabled={isLocked} />
            </div>
          )}
        </section>
      </div>
    </aside>
  );
}

const ActionBtn = ({ icon: Icon, label, onClick, testId, tone = "default", disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`h-9 rounded-lg border text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
      tone === "red"
        ? "border-[#E4E7EC] bg-white text-[#e63946] hover:border-[#e63946]"
        : "border-[#E4E7EC] bg-white text-[#1d3557] hover:border-[#457b9d] hover:bg-[#F1FAEE]"
    } disabled:opacity-40 disabled:cursor-not-allowed`}
  >
    <Icon size={12} />
    {label}
  </button>
);

// ---------- Mobile sticky footer ----------
function MobileStickyFooter({ totals, onPreview, onSave, saving }) {
  return (
    <div className="fixed lg:hidden bottom-0 left-0 right-0 bg-white border-t border-[#E4E7EC] p-3 z-30 flex items-center gap-2 shadow-lg" data-testid="est-mobile-footer">
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#667085]">Total</div>
        <div className="text-[18px] font-bold font-mono text-[#1d3557] leading-tight" data-testid="est-mobile-total">
          {inr(totals.grand_total)}
        </div>
      </div>
      <button
        type="button"
        onClick={onPreview}
        className="h-11 px-4 rounded-lg border border-[#E4E7EC] bg-white text-[12.5px] font-semibold text-[#1d3557]"
      >
        Preview
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        data-testid="est-mobile-save"
        className="h-11 px-4 rounded-lg bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-60"
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
